import { useSuspenseQuery } from '@apollo/client/react';
import {
  Absatz,
  AbsatzProduktivitaet,
  Currency,
  Money,
  Umsatz,
} from '../entities';
import { GET_HISTORIE_FOR_PLANWERTE_DIALOG } from './graphql/queries';
import * as Sentry from '@sentry/react';
import { Data, Historie, TagebuchEintragHistorie } from '../entities/historie';
import {
  TagebuchEintragData,
  Controlling_Absatz as GqlAbsatz,
  Umsatz as GqlUmsatz,
  TagebuchEintragWetter,
  GetHistorieByStandortIdAndDatumQuery,
  GetHistorieByStandortIdAndDatumQueryVariables,
} from '@/gql/graphql';
import { Wetter } from '../entities/historie/wetter';

function getAbsatz(absatzData?: GqlAbsatz | null): Absatz | undefined {
  if (!absatzData) {
    return undefined;
  }

  return new Absatz({
    wert: absatzData.wert ?? undefined,
    produktivitaet: new AbsatzProduktivitaet({
      mpt: absatzData.produktivitaet?.mpt ?? undefined,
      stueck: absatzData.produktivitaet?.stueck ?? undefined,
    }),
  });
}

function getUmsatz(umsatzData?: GqlUmsatz | null): Umsatz | undefined {
  if (!umsatzData) {
    return undefined;
  }

  return new Umsatz({
    wert: umsatzData.wert
      ? new Money({
          amount: umsatzData.wert.amount,
          currency: Currency[umsatzData.wert.currency],
        })
      : undefined,
    produktivitaet: umsatzData.produktivitaet
      ? new Money({
          amount: umsatzData.produktivitaet.amount,
          currency: Currency[umsatzData.produktivitaet.currency],
        })
      : undefined,
  });
}

function getWetter(
  wetterData?: TagebuchEintragWetter | null,
): Wetter | undefined {
  if (!wetterData) {
    return undefined;
  }

  return new Wetter({
    code: wetterData.code ?? undefined,
    maxTemperatur: wetterData.maxTemperatur ?? undefined,
    minTemperatur: wetterData.minTemperatur ?? undefined,
  });
}

function getData(
  tagebuchEintragData?: TagebuchEintragData | null,
): Data | undefined {
  if (!tagebuchEintragData) {
    return undefined;
  }

  return new Data({
    datum: tagebuchEintragData.datum,
    interneNotiz: tagebuchEintragData.interneNotiz,
    istAbsatz: getAbsatz(tagebuchEintragData.istAbsatz),
    planAbsatz: getAbsatz(tagebuchEintragData.planAbsatz),
    istUmsatz: getUmsatz(tagebuchEintragData.istUmsatz),
    planUmsatz: getUmsatz(tagebuchEintragData.planUmsatz),
    wetter: getWetter(tagebuchEintragData.wetter),
  });
}

export function useHistorie({
  standortId,
  datum,
}: {
  standortId: string;
  datum: string;
}): {
  error?: string;
  tagebuchEintragHistorie?: TagebuchEintragHistorie;
} {
  const { data, error } = useSuspenseQuery<
    GetHistorieByStandortIdAndDatumQuery,
    GetHistorieByStandortIdAndDatumQueryVariables
  >(GET_HISTORIE_FOR_PLANWERTE_DIALOG, {
    variables: {
      standortId,
      datum,
    },
    fetchPolicy: 'network-only',
  });

  if (error) {
    Sentry.captureException(error);
    return { error: error?.message };
  }

  const newHistorie = new Historie({
    current: getData(data.historieByStandortIdAndDatum.historie.current),
    prevWeek: getData(data.historieByStandortIdAndDatum.historie.prevWeek),
    prevMonth: getData(data.historieByStandortIdAndDatum.historie.prevMonth),
    prevYear: getData(data.historieByStandortIdAndDatum.historie.prevYear),
  });

  const newTagebuchEintragHistorie = new TagebuchEintragHistorie({
    datum: datum,
    historie: newHistorie,
  });

  return { tagebuchEintragHistorie: newTagebuchEintragHistorie };
}
